import React from 'react';
import { useAtom } from 'jotai';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';

import { contactSectionsHydratedAtom, contactSectionsListParamsAtom } from '@/store';

export interface ContactSectionSearchFields {
  name: string;
  roles: string;
}

export function useContactSectionsFilters() {
  const [params, setParams] = useAtom(contactSectionsListParamsAtom);
  const [, setHydrated] = useAtom(contactSectionsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    sort: parseAsString,
    include: parseAsString,
    'filter[name]': parseAsString,
    'filter[roles]': parseAsString
  });

  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};
    if (queryParams['filter[name]']) filters.name = queryParams['filter[name]'] || undefined;
    if (queryParams['filter[roles]']) filters.roles = queryParams['filter[roles]'] || undefined;

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      per_page: queryParams.per_page,
      sort: queryParams.sort || undefined,
      filters,
      include: queryParams.include || prev.include
    }));

    setHydrated(true);
  }, [
    queryParams.page,
    queryParams.per_page,
    queryParams.sort,
    queryParams.include,
    queryParams['filter[name]'],
    queryParams['filter[roles]'],
    setParams,
    setHydrated
  ]);

  const searchFields: ContactSectionSearchFields = {
    name: params.filters.name || '',
    roles: params.filters.roles || ''
  };

  const setSearchFields = React.useCallback(
    (updater: (prev: ContactSectionSearchFields) => ContactSectionSearchFields) => {
      const next = updater(searchFields);
      setQueryParams({
        'filter[name]': next.name || null,
        'filter[roles]': next.roles || null,
        page: 1
      });
    },
    [searchFields, setQueryParams]
  );

  const clearFilters = React.useCallback(() => {
    setQueryParams({
      'filter[name]': null,
      'filter[roles]': null,
      page: 1
    });
  }, [setQueryParams]);

  const applyFilters = React.useCallback(() => {
    setQueryParams({
      'filter[name]': searchFields.name || null,
      'filter[roles]': searchFields.roles || null,
      page: 1
    });
  }, [searchFields, setQueryParams]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setQueryParams({ page });
    },
    [setQueryParams]
  );

  const sortParam = params.sort;
  const setSortParam = React.useCallback(
    (sortValue: string | null) => {
      setQueryParams({ sort: sortValue || null, page: 1 });
    },
    [setQueryParams]
  );

  const includeParam = params.include;
  const setIncludeParam = React.useCallback(
    (value: string | null) => {
      setQueryParams({ include: value || null });
    },
    [setQueryParams]
  );

  const hasAnyFilter = Boolean(queryParams['filter[name]'] || queryParams['filter[roles]']);

  return {
    searchFields,
    setSearchFields,
    clearFilters,
    applyFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam,
    includeParam,
    setIncludeParam
  };
}

